import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "../axiosConfig";

const REMINDER_OPTIONS = [
  { value: 0, label: "No reminder" },
  { value: 5, label: "5 minutes before" },
  { value: 15, label: "15 minutes before" },
  { value: 30, label: "30 minutes before" },
  { value: 60, label: "1 hour before" },
  { value: 1440, label: "1 day before" },
];

const RECURRENCE_OPTIONS = [
  { value: "none", label: "Does not repeat" },
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "biweekly", label: "Every 2 weeks" },
  { value: "monthly", label: "Monthly" },
];

const CALENDAR_TRANSFER_STATUS_KEY = "addanote.calendar.transferStatus.v1";
const CALENDAR_IMPORT_CHUNK_EVENT_LIMIT = 25;
const CALENDAR_IMPORT_CHUNK_CHAR_LIMIT = 250_000;

function pad(value) {
  return String(value).padStart(2, "0");
}

function startOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function endOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0);
}

function formatMonthLabel(date) {
  return new Intl.DateTimeFormat("en-US", { month: "long", year: "numeric" }).format(date);
}

function formatEventDateTime(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}

function toLocalDateTimeInputValue(value) {
  const date = value ? new Date(value) : new Date();
  if (Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1);
  const day = pad(date.getDate());
  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

function toLocalDateInputValue(value) {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return "";
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function createDefaultEventForm(date = new Date()) {
  const start = new Date(date);
  start.setHours(9, 0, 0, 0);
  const end = new Date(start);
  end.setHours(start.getHours() + 1);
  return {
    title: "",
    description: "",
    startAt: toLocalDateTimeInputValue(start),
    endAt: toLocalDateTimeInputValue(end),
    reminderMinutes: "15",
    reminderEmailEnabled: true,
    reminderTextEnabled: true,
    recurrenceRule: "none",
    recurrenceUntil: "",
  };
}

function getEventReminderLabel(event) {
  const option = REMINDER_OPTIONS.find((entry) => entry.value === Number(event?.reminderMinutes || 0));
  return option?.label || "Custom reminder";
}

function getEventReminderChannelLabel(event) {
  const emailEnabled = event?.reminderEmailEnabled !== false;
  const textEnabled = event?.reminderTextEnabled !== false;
  if (emailEnabled && textEnabled) return "Email + text";
  if (emailEnabled) return "Email only";
  if (textEnabled) return "Text only";
  return "No delivery method";
}

function getEventRecurrenceLabel(event) {
  const rule = String(event?.recurrenceRule || "none");
  if (rule === "daily") return "Repeats daily";
  if (rule === "weekly") return "Repeats weekly";
  if (rule === "biweekly") return "Repeats every 2 weeks";
  if (rule === "monthly") return "Repeats monthly";
  return "One time";
}

function getReminderAt(event) {
  const reminderMinutes = Number(event?.reminderMinutes || 0);
  if (!reminderMinutes) return null;
  const startAt = new Date(event.startAt);
  if (Number.isNaN(startAt.getTime())) return null;
  return new Date(startAt.getTime() - reminderMinutes * 60 * 1000);
}

function normalizeSearchValue(value) {
  return String(value || "").trim().toLowerCase();
}

function eventMatchesQuery(event, query) {
  const normalizedQuery = normalizeSearchValue(query);
  if (!normalizedQuery) return true;

  const haystack = [
    event?.title || "",
    event?.description || "",
    formatEventDateTime(event?.startAt || ""),
    formatEventDateTime(event?.endAt || ""),
  ].join(" ").toLowerCase();

  return haystack.includes(normalizedQuery);
}

function buildMonthGrid(monthDate) {
  const first = startOfMonth(monthDate);
  const last = endOfMonth(monthDate);
  const gridStart = new Date(first);
  gridStart.setDate(first.getDate() - first.getDay());
  const gridEnd = new Date(last);
  gridEnd.setDate(last.getDate() + (6 - last.getDay()));

  const days = [];
  for (const cursor = new Date(gridStart); cursor <= gridEnd; cursor.setDate(cursor.getDate() + 1)) {
    days.push(new Date(cursor));
  }
  return days;
}

function formatTransferTimestamp(value) {
  const date = new Date(value || "");
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}

function readCalendarTransferStatus() {
  if (typeof window === "undefined") return { lastImportAt: "", lastExportAt: "" };
  try {
    const parsed = JSON.parse(window.localStorage.getItem(CALENDAR_TRANSFER_STATUS_KEY) || "{}");
    return {
      lastImportAt: String(parsed?.lastImportAt || ""),
      lastExportAt: String(parsed?.lastExportAt || ""),
    };
  } catch {
    return { lastImportAt: "", lastExportAt: "" };
  }
}

function splitIcsIntoChunks(icsText) {
  const normalized = String(icsText || "").replace(/\r\n/g, "\n");
  const blocks = normalized.match(/BEGIN:VEVENT[\s\S]*?END:VEVENT/g) || [];
  if (!blocks.length) return [];

  const chunks = [];
  let currentBlocks = [];
  let currentSize = 0;

  for (const block of blocks) {
    const nextSize = currentSize + block.length;
    if (
      currentBlocks.length
      && (currentBlocks.length >= CALENDAR_IMPORT_CHUNK_EVENT_LIMIT || nextSize > CALENDAR_IMPORT_CHUNK_CHAR_LIMIT)
    ) {
      chunks.push(currentBlocks);
      currentBlocks = [];
      currentSize = 0;
    }
    currentBlocks.push(block);
    currentSize += block.length;
  }

  if (currentBlocks.length) {
    chunks.push(currentBlocks);
  }

  return chunks.map((eventBlocks) => [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//AddaNote//Calendar Import//EN",
    ...eventBlocks,
    "END:VCALENDAR",
    "",
  ].join("\r\n"));
}

async function fetchMonthEvents(monthRange) {
  const res = await api.get(`/api/calendar/events?from=${encodeURIComponent(monthRange.from)}&to=${encodeURIComponent(monthRange.to)}`);
  return Array.isArray(res.data?.events) ? res.data.events : [];
}

export default function CalendarView() {
  const [monthDate, setMonthDate] = useState(() => startOfMonth(new Date()));
  const [selectedDate, setSelectedDate] = useState(() => new Date());
  const [browserNow, setBrowserNow] = useState(() => new Date());
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [saveErrorMessage, setSaveErrorMessage] = useState("");
  const [saving, setSaving] = useState(false);
  const [importMessage, setImportMessage] = useState("");
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [transferStatus, setTransferStatus] = useState(() => readCalendarTransferStatus());
  const [googleCalendar, setGoogleCalendar] = useState({ configured: false, connected: false, lastSyncedAt: null });
  const [googleCalendarBusy, setGoogleCalendarBusy] = useState(false);
  const [googleCalendarMessage, setGoogleCalendarMessage] = useState("");
  const [editingEventId, setEditingEventId] = useState("");
  const [editingScope, setEditingScope] = useState("series");
  const [editingOccurrenceStart, setEditingOccurrenceStart] = useState("");
  const [editingOccurrenceId, setEditingOccurrenceId] = useState("");
  const [form, setForm] = useState(() => createDefaultEventForm(new Date()));
  const [eventQuery, setEventQuery] = useState("");
  const importInputRef = useRef(null);

  const monthFrom = useMemo(() => startOfMonth(monthDate).toISOString(), [monthDate]);
  const monthTo = useMemo(() => new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 1).toISOString(), [monthDate]);
  const monthRange = useMemo(() => ({ from: monthFrom, to: monthTo }), [monthFrom, monthTo]);

  const days = useMemo(() => buildMonthGrid(monthDate), [monthDate]);
  const selectedDateKey = `${selectedDate.getFullYear()}-${pad(selectedDate.getMonth() + 1)}-${pad(selectedDate.getDate())}`;
  const browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || "Local time";
  const browserNowLabel = new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  }).format(browserNow);

  const filteredEvents = useMemo(
    () => [...events].sort((left, right) => new Date(left.startAt).getTime() - new Date(right.startAt).getTime()),
    [events],
  );

  const selectedDayEvents = useMemo(
    () => filteredEvents.filter((event) => {
      const date = new Date(event.startAt);
      if (Number.isNaN(date.getTime())) return false;
      const key = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
      return key === selectedDateKey;
    }),
    [filteredEvents, selectedDateKey],
  );
  const matchingEvents = useMemo(
    () => filteredEvents.filter((event) => eventMatchesQuery(event, eventQuery)),
    [eventQuery, filteredEvents],
  );
  const displayedEvents = eventQuery ? matchingEvents : selectedDayEvents;

  useEffect(() => {
    let ignore = false;

    const loadEvents = async () => {
      setLoading(true);
      setErrorMessage("");
      try {
        const nextEvents = await fetchMonthEvents(monthRange);
        if (!ignore) {
          setEvents(nextEvents);
        }
      } catch (error) {
        if (!ignore) {
          setErrorMessage(error.response?.data?.error || "Could not load calendar events.");
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    };

    loadEvents();
    return () => {
      ignore = true;
    };
  }, [monthRange]);

  useEffect(() => {
    let ignore = false;
    api.get("/api/calendar/google/status")
      .then((res) => {
        if (!ignore) setGoogleCalendar({
          configured: Boolean(res.data?.configured),
          connected: Boolean(res.data?.connected),
          lastSyncedAt: res.data?.lastSyncedAt || null,
        });
      })
      .catch(() => {
        if (!ignore) setGoogleCalendarMessage("Google Calendar status is unavailable.");
      });
    return () => { ignore = true; };
  }, []);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setBrowserNow(new Date());
    }, 60_000);
    return () => window.clearInterval(intervalId);
  }, []);

  const resetForm = (date = selectedDate) => {
    setEditingEventId("");
    setEditingScope("series");
    setEditingOccurrenceStart("");
    setEditingOccurrenceId("");
    setForm(createDefaultEventForm(date));
    setSaveErrorMessage("");
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    if (date.getFullYear() !== monthDate.getFullYear() || date.getMonth() !== monthDate.getMonth()) {
      setMonthDate(startOfMonth(date));
    }
    if (!editingEventId) {
      setForm(createDefaultEventForm(date));
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setSaveErrorMessage("");

    try {
      const payload = {
        title: form.title.trim(),
        description: form.description.trim(),
        startAt: new Date(form.startAt).toISOString(),
        endAt: new Date(form.endAt).toISOString(),
        reminderMinutes: Number(form.reminderMinutes || 0),
        reminderEmailEnabled: Boolean(form.reminderEmailEnabled),
        reminderTextEnabled: Boolean(form.reminderTextEnabled),
        recurrenceRule: form.recurrenceRule || "none",
        recurrenceUntil: form.recurrenceRule !== "none" && form.recurrenceUntil
          ? new Date(`${form.recurrenceUntil}T23:59:59`).toISOString()
          : null,
      };

      if (!payload.title || Number.isNaN(new Date(payload.startAt).getTime()) || Number.isNaN(new Date(payload.endAt).getTime())) {
        throw new Error("Title, start time, and end time are required.");
      }

      if (new Date(payload.endAt).getTime() < new Date(payload.startAt).getTime()) {
        throw new Error("End time must be after start time.");
      }
      if (payload.reminderMinutes > 0 && !payload.reminderEmailEnabled && !payload.reminderTextEnabled) {
        throw new Error("Select at least one reminder delivery method.");
      }
      if (payload.recurrenceRule !== "none" && payload.recurrenceUntil && new Date(payload.recurrenceUntil).getTime() < new Date(payload.startAt).getTime()) {
        throw new Error("Recurring events must end on or after the first occurrence.");
      }

      const res = editingEventId
        ? editingScope === "occurrence"
          ? await api.put(`/api/calendar/events/${editingEventId}/occurrences`, {
            ...payload,
            occurrenceStart: editingOccurrenceStart,
          })
          : await api.put(`/api/calendar/events/${editingEventId}`, payload)
        : await api.post("/api/calendar/events", payload);

      const savedEvent = res.data?.event;
      if (!savedEvent) {
        throw new Error(`Could not ${editingEventId ? "update" : "create"} the calendar event.`);
      }

      setEvents((current) => {
        const next = current.filter((entry) => entry.id !== savedEvent.id && entry.id !== editingOccurrenceId);
        return [...next, savedEvent];
      });
      const savedEventDate = new Date(savedEvent.startAt);
      setMonthDate(startOfMonth(savedEventDate));
      resetForm(savedEventDate);
      setSelectedDate(savedEventDate);
    } catch (error) {
      setSaveErrorMessage(error.response?.data?.error || error.message || "Could not save the calendar event.");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (event, scope = "series") => {
    setEditingEventId(event.seriesId || event.id);
    setEditingScope(scope);
    setEditingOccurrenceStart(event.occurrenceStart || event.startAt);
    setEditingOccurrenceId(event.id);
    setForm({
      title: event.title || "",
      description: event.description || "",
      startAt: toLocalDateTimeInputValue(event.startAt),
      endAt: toLocalDateTimeInputValue(event.endAt),
      reminderMinutes: String(event.reminderMinutes || 0),
      reminderEmailEnabled: event.reminderEmailEnabled !== false,
      reminderTextEnabled: event.reminderTextEnabled !== false,
      recurrenceRule: event.recurrenceRule || "none",
      recurrenceUntil: toLocalDateInputValue(event.recurrenceUntil),
    });
    setSaveErrorMessage("");
  };

  const handleSkipOccurrence = async (event) => {
    try {
      await api.post(`/api/calendar/events/${event.seriesId || event.id}/occurrences/skip`, {
        occurrenceStart: event.occurrenceStart || event.startAt,
      });
      setEvents((current) => current.filter((entry) => entry.id !== event.id));
      if (editingOccurrenceId === event.id) {
        resetForm(selectedDate);
      }
    } catch (error) {
      setSaveErrorMessage(error.response?.data?.error || "Could not skip this occurrence.");
    }
  };

  const handleDelete = async (event) => {
    const eventId = event.seriesId || event.id;
    const isSeries = Boolean(event.isRecurring);
    const confirmed = window.confirm(
      isSeries
        ? `Delete the entire recurring series \"${event.title}\"? This cannot be undone.`
        : `Delete \"${event.title}\"? This cannot be undone.`,
    );
    if (!confirmed) return;

    try {
      await api.delete(`/api/calendar/events/${eventId}`);
      setEvents((current) => current.filter((entry) => (entry.seriesId || entry.id) !== eventId));
      if (editingEventId === eventId) {
        resetForm(selectedDate);
      }
    } catch (error) {
      setSaveErrorMessage(error.response?.data?.error || "Could not delete the calendar event.");
    }
  };

  const updateTransferStatus = (nextStatus) => {
    setTransferStatus(nextStatus);
    if (typeof window !== "undefined") {
      window.localStorage.setItem(CALENDAR_TRANSFER_STATUS_KEY, JSON.stringify(nextStatus));
    }
  };

  const handleExport = async () => {
    setExporting(true);
    setImportMessage("");
    try {
      const res = await api.get("/api/calendar/export", { responseType: "blob" });
      const blob = res.data instanceof Blob ? res.data : new Blob([res.data], { type: "text/calendar;charset=utf-8" });
      const downloadUrl = window.URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = downloadUrl;
      anchor.download = `calendar-${new Date().toISOString().slice(0, 10)}.ics`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(downloadUrl);
      updateTransferStatus({
        ...transferStatus,
        lastExportAt: new Date().toISOString(),
      });
    } catch (error) {
      setImportMessage(error.response?.data?.error || "Could not export calendar events.");
    } finally {
      setExporting(false);
    }
  };

  const handleImportButtonClick = () => {
    importInputRef.current?.click();
  };

  const handleGoogleConnect = async () => {
    setGoogleCalendarBusy(true);
    setGoogleCalendarMessage("");
    try {
      const res = await api.get("/api/calendar/google/connect");
      if (!res.data?.authorizationUrl) throw new Error("Could not start Google Calendar connection.");
      window.location.assign(res.data.authorizationUrl);
    } catch (error) {
      setGoogleCalendarMessage(error.response?.data?.error || error.message || "Could not connect Google Calendar.");
      setGoogleCalendarBusy(false);
    }
  };

  const handleGoogleSync = async () => {
    setGoogleCalendarBusy(true);
    setGoogleCalendarMessage("");
    try {
      const res = await api.post("/api/calendar/google/sync");
      const sync = res.data || {};
      setGoogleCalendar((current) => ({ ...current, connected: true, lastSyncedAt: new Date().toISOString() }));
      setGoogleCalendarMessage(`Google Calendar synced: ${Number(sync.imported || 0)} added, ${Number(sync.updated || 0)} updated, ${Number(sync.removed || 0)} removed.`);
      setEvents(await fetchMonthEvents(monthRange));
    } catch (error) {
      setGoogleCalendarMessage(error.response?.data?.error || "Could not sync Google Calendar.");
    } finally {
      setGoogleCalendarBusy(false);
    }
  };

  const handleGoogleDisconnect = async () => {
    if (!window.confirm("Disconnect Google Calendar? Events already imported into AddaNote will remain.")) return;
    setGoogleCalendarBusy(true);
    setGoogleCalendarMessage("");
    try {
      await api.delete("/api/calendar/google/connect");
      setGoogleCalendar({ configured: googleCalendar.configured, connected: false, lastSyncedAt: null });
      setGoogleCalendarMessage("Google Calendar disconnected. Existing imported events remain in AddaNote.");
    } catch (error) {
      setGoogleCalendarMessage(error.response?.data?.error || "Could not disconnect Google Calendar.");
    } finally {
      setGoogleCalendarBusy(false);
    }
  };

  const handleImportFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    setImporting(true);
    setImportMessage("");
    try {
      const icsText = await file.text();
      const chunks = splitIcsIntoChunks(icsText);
      if (!chunks.length) throw new Error("Import file does not contain any calendar events.");

      let previewCount = 0;
      let duplicateCount = 0;
      let newCount = 0;
      for (const chunk of chunks) {
        const previewRes = await api.post("/api/calendar/import-preview", { icsText: chunk });
        previewCount += Number(previewRes.data?.foundCount || 0);
        duplicateCount += Number(previewRes.data?.duplicateCount || 0);
        newCount += Number(previewRes.data?.newCount || 0);
      }
      if (!previewCount) throw new Error("Import file does not contain any calendar events.");
      const confirmed = window.confirm(
        `Import preview:\n\n${previewCount} event${previewCount === 1 ? "" : "s"} found.\n${newCount} new.\n${duplicateCount} likely duplicate${duplicateCount === 1 ? "" : "s"}.\n\nContinue with import?`,
      );
      if (!confirmed) {
        setImportMessage("Import canceled.");
        return;
      }
      let importedCount = 0;
      for (const chunk of chunks) {
        const res = await api.post("/api/calendar/import", { icsText: chunk });
        importedCount += Number(res.data?.importedCount || 0);
      }
      const nextEvents = await fetchMonthEvents(monthRange);
      setEvents(nextEvents);
      setImportMessage(`Imported ${importedCount} event${importedCount === 1 ? "" : "s"}.`);
      updateTransferStatus({
        ...transferStatus,
        lastImportAt: new Date().toISOString(),
      });
    } catch (error) {
      setImportMessage(error.response?.data?.error || "Could not import calendar events.");
    } finally {
      setImporting(false);
    }
  };

  return (
    <section className="calendar-shell">
      <div className="section-heading section-heading-compact">
        <div>
          <span className="eyebrow">Scheduling</span>
          <h2>Calendar</h2>
        </div>
        <div className="calendar-header-actions">
          <button type="button" className="action-button action-button-secondary" onClick={() => setMonthDate(new Date(monthDate.getFullYear(), monthDate.getMonth() - 1, 1))}>
            Previous month
          </button>
          <strong className="calendar-month-label">{formatMonthLabel(monthDate)}</strong>
          <button type="button" className="action-button action-button-secondary" onClick={() => setMonthDate(new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 1))}>
            Next month
          </button>
        </div>
      </div>

      <div className="calendar-layout">
        <div className="calendar-panel">
          <div className="calendar-weekdays" aria-hidden="true">
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((label) => (
              <span key={label}>{label}</span>
            ))}
          </div>
          <div className="calendar-grid">
            {days.map((day) => {
              const dayKey = `${day.getFullYear()}-${pad(day.getMonth() + 1)}-${pad(day.getDate())}`;
              const dayEvents = filteredEvents.filter((event) => {
                const date = new Date(event.startAt);
                if (Number.isNaN(date.getTime())) return false;
                return date.getFullYear() === day.getFullYear()
                  && date.getMonth() === day.getMonth()
                  && date.getDate() === day.getDate();
              });
              const isCurrentMonth = day.getMonth() === monthDate.getMonth();
              const isSelected = dayKey === selectedDateKey;
              return (
                <button
                  key={day.toISOString()}
                  type="button"
                  className={`calendar-day-card${isCurrentMonth ? "" : " is-outside-month"}${isSelected ? " is-selected" : ""}`}
                  onClick={() => handleDateSelect(new Date(day))}
                >
                  <span className="calendar-day-number">{day.getDate()}</span>
                  <span className="calendar-day-count">{dayEvents.length ? `${dayEvents.length} event${dayEvents.length === 1 ? "" : "s"}` : "No events"}</span>
                  {dayEvents.length ? (
                    <div className="calendar-day-events">
                      {dayEvents.slice(0, 3).map((event, index) => {
                        const reminderAt = getReminderAt(event);
                        return (
                          <div key={event.id} className="calendar-day-event-pill">
                            <strong>Event {index + 1}</strong>
                            <span>{event.title}</span>
                            <div className="calendar-day-event-popover" role="tooltip">
                              <strong>{event.title}</strong>
                              <span>{formatEventDateTime(event.startAt)} to {formatEventDateTime(event.endAt)}</span>
                              {event.description ? <p>{event.description}</p> : null}
                              <small>
                                {getEventRecurrenceLabel(event)}
                                {` · ${getEventReminderLabel(event)}`}
                                {` · ${getEventReminderChannelLabel(event)}`}
                                {reminderAt ? ` · ${formatEventDateTime(reminderAt)}` : ""}
                              </small>
                            </div>
                          </div>
                        );
                      })}
                      {dayEvents.length > 3 ? (
                        <small className="calendar-day-event-more">+{dayEvents.length - 3} more</small>
                      ) : null}
                    </div>
                  ) : null}
                </button>
              );
            })}
          </div>
          {loading ? <div className="feedback-panel">Loading calendar events...</div> : null}
          {errorMessage ? <div className="feedback-panel error">{errorMessage}</div> : null}
        </div>

        <div className="calendar-side-column">
          <section className="calendar-panel calendar-form-panel">
            <div className="section-heading section-heading-compact">
              <div>
                <span className="eyebrow">{editingEventId ? (editingScope === "occurrence" ? "Edit occurrence" : "Edit series") : "New event"}</span>
                <h3>{editingEventId ? (editingScope === "occurrence" ? "Update occurrence" : "Update schedule") : "Schedule event"}</h3>
              </div>
            </div>
            <p className="text-muted">Choose whether each reminder goes by email, text, or both. Text delivery uses the phone and carrier saved in Profile Settings.</p>
            <section className="import-export-panel">
              <span className="stat-label">Google Calendar</span>
              {googleCalendar.connected ? (
                <>
                  <p className="text-muted">Connected. New and updated Google Calendar events sync automatically every five minutes.</p>
                  {googleCalendar.lastSyncedAt ? <small>Last synced: {formatTransferTimestamp(googleCalendar.lastSyncedAt)}</small> : null}
                  <div className="calendar-form-actions">
                    <button type="button" className="action-button action-button-secondary" onClick={handleGoogleSync} disabled={googleCalendarBusy}>
                      {googleCalendarBusy ? "Syncing..." : "Sync Google Calendar now"}
                    </button>
                    <button type="button" className="action-link danger" onClick={handleGoogleDisconnect} disabled={googleCalendarBusy}>Disconnect</button>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-muted">Connect Google Calendar to automatically import new and updated events into AddaNote.</p>
                  <button type="button" className="action-button action-button-secondary" onClick={handleGoogleConnect} disabled={googleCalendarBusy || !googleCalendar.configured}>
                    {googleCalendarBusy ? "Connecting..." : "Connect Google Calendar"}
                  </button>
                  {!googleCalendar.configured ? <small>Google Calendar connection is not configured yet.</small> : null}
                </>
              )}
              {googleCalendarMessage ? <div className="feedback-panel">{googleCalendarMessage}</div> : null}
            </section>
            <section className="import-export-panel">
              <span className="stat-label">Import / export</span>
              <p className="text-muted">Download your calendar as an `.ics` file or upload an `.ics` file to add events.</p>
              <div className="calendar-form-actions">
                <button type="button" className="action-button action-button-secondary" onClick={handleExport} disabled={exporting || importing}>
                  {exporting ? "Downloading..." : "Download .ics"}
                </button>
                <button type="button" className="action-button action-button-secondary" onClick={handleImportButtonClick} disabled={importing || exporting}>
                  {importing ? "Uploading..." : "Upload .ics file"}
                </button>
                <input
                  ref={importInputRef}
                  type="file"
                  accept=".ics,text/calendar"
                  style={{ display: "none" }}
                  onChange={handleImportFile}
                  aria-label="Import calendar file"
                />
              </div>
            </section>
            {(transferStatus.lastImportAt || transferStatus.lastExportAt) ? (
              <div className="import-export-status">
                {transferStatus.lastExportAt ? <span>Last export: {formatTransferTimestamp(transferStatus.lastExportAt)}</span> : null}
                {transferStatus.lastImportAt ? <span>Last import: {formatTransferTimestamp(transferStatus.lastImportAt)}</span> : null}
              </div>
            ) : null}
            {importMessage ? <div className={`feedback-panel${importMessage.startsWith("Imported ") ? "" : " error"}`}>{importMessage}</div> : null}

            <form className="calendar-form" onSubmit={handleSubmit}>
              <label className="editor-field">
                <span>Title</span>
                <input
                  name="title"
                  value={form.title}
                  onChange={(event) => setForm((current) => ({ ...current, title: event.target.value }))}
                  placeholder="Team sync"
                />
              </label>
              <label className="editor-field">
                <span>Description</span>
                <textarea
                  className="calendar-description-input"
                  name="description"
                  value={form.description}
                  onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))}
                  rows={4}
                  placeholder="Agenda, location, or meeting note"
                />
              </label>
              <label className="editor-field">
                <span>Start</span>
                <input
                  type="datetime-local"
                  name="startAt"
                  value={form.startAt}
                  onChange={(event) => setForm((current) => ({ ...current, startAt: event.target.value }))}
                />
              </label>
              <label className="editor-field">
                <span>End</span>
                <input
                  type="datetime-local"
                  name="endAt"
                  value={form.endAt}
                  onChange={(event) => setForm((current) => ({ ...current, endAt: event.target.value }))}
                />
              </label>
              <label className="editor-field">
                <span>Reminder</span>
                <select
                  name="reminderMinutes"
                  value={form.reminderMinutes}
                  onChange={(event) => setForm((current) => ({ ...current, reminderMinutes: event.target.value }))}
                >
                  {REMINDER_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </label>
              <label className="editor-field">
                <span>Repeats</span>
                <select
                  name="recurrenceRule"
                  value={form.recurrenceRule}
                  onChange={(event) => setForm((current) => ({
                    ...current,
                    recurrenceRule: event.target.value,
                    recurrenceUntil: event.target.value === "none" ? "" : current.recurrenceUntil,
                  }))}
                >
                  {RECURRENCE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </label>
              {form.recurrenceRule !== "none" ? (
                <label className="editor-field">
                  <span>Repeat until (optional end date)</span>
                  <input
                    type="date"
                    name="recurrenceUntil"
                    value={form.recurrenceUntil}
                    onChange={(event) => setForm((current) => ({ ...current, recurrenceUntil: event.target.value }))}
                  />
                  <small>This date is the last day the recurring event should appear. Leave it blank to continue indefinitely until you update or delete the event.</small>
                </label>
              ) : null}
              <div className="import-export-panel">
                <span className="stat-label">Reminder delivery</span>
                <label className="profile-checkbox">
                  <input
                    type="checkbox"
                    name="reminderEmailEnabled"
                    checked={form.reminderEmailEnabled}
                    onChange={(event) => setForm((current) => ({ ...current, reminderEmailEnabled: event.target.checked }))}
                  />
                  <span>Email reminder</span>
                </label>
                <label className="profile-checkbox">
                  <input
                    type="checkbox"
                    name="reminderTextEnabled"
                    checked={form.reminderTextEnabled}
                    onChange={(event) => setForm((current) => ({ ...current, reminderTextEnabled: event.target.checked }))}
                  />
                  <span>Text reminder</span>
                </label>
              </div>

              {saveErrorMessage ? <div className="feedback-panel error">{saveErrorMessage}</div> : null}

              <div className="calendar-form-actions">
                <button type="submit" className="action-button" disabled={saving}>
                  {editingEventId ? (editingScope === "occurrence" ? "Update occurrence" : "Update event") : "Create event"}
                </button>
                <button type="button" className="action-button action-button-secondary" onClick={() => resetForm(selectedDate)} disabled={saving}>
                  Clear
                </button>
              </div>
            </form>
          </section>

          <section className="calendar-panel calendar-events-panel">
            <div className="section-heading section-heading-compact">
              <div>
                <span className="eyebrow">{eventQuery ? "Event search" : "Selected day"}</span>
                <h3>{eventQuery ? "Matching events" : new Intl.DateTimeFormat("en-US", { month: "long", day: "numeric", year: "numeric" }).format(selectedDate)}</h3>
              </div>
              <div className="calendar-selected-day-meta">
                <strong>{browserTimeZone}</strong>
                <span>{browserNowLabel}</span>
              </div>
            </div>
            <label className="calendar-event-search">
              <span>Search events</span>
              <input
                type="search"
                value={eventQuery}
                onChange={(event) => setEventQuery(event.target.value)}
                placeholder="Search title, note, or event time"
              />
            </label>

            {!displayedEvents.length ? (
              <div className="calendar-empty-state">
                {eventQuery ? "No events match this search." : "No scheduled events for this day."}
              </div>
            ) : (
              <div className="calendar-event-list">
                {displayedEvents.map((event) => {
                  const reminderAt = getReminderAt(event);
                  return (
                    <article key={event.id} className="calendar-event-card">
                      <div className="calendar-event-copy">
                        <strong>{event.title}</strong>
                        <span>{formatEventDateTime(event.startAt)} to {formatEventDateTime(event.endAt)}</span>
                        {event.description ? <p>{event.description}</p> : null}
                        <small>
                          {getEventRecurrenceLabel(event)}
                          {` · ${getEventReminderLabel(event)}`}
                          {` · ${getEventReminderChannelLabel(event)}`}
                          {reminderAt ? ` · ${formatEventDateTime(reminderAt)}` : ""}
                        </small>
                      </div>
                      <div className="calendar-event-popover" role="tooltip">
                        <strong>{event.title}</strong>
                        <span>{formatEventDateTime(event.startAt)} to {formatEventDateTime(event.endAt)}</span>
                        {event.description ? <p>{event.description}</p> : null}
                        <small>
                          {getEventRecurrenceLabel(event)}
                          {` · ${getEventReminderLabel(event)}`}
                          {` · ${getEventReminderChannelLabel(event)}`}
                          {reminderAt ? ` · ${formatEventDateTime(reminderAt)}` : ""}
                        </small>
                      </div>
                      <div className="calendar-event-actions">
                        {event.isRecurring ? (
                          <>
                            <button type="button" className="action-link" onClick={() => handleEdit(event, "occurrence")}>Edit occurrence</button>
                            <button type="button" className="action-link" onClick={() => handleEdit(event, "series")}>Edit series</button>
                            <button type="button" className="action-link" onClick={() => handleSkipOccurrence(event)}>Skip once</button>
                            <button type="button" className="action-link danger" onClick={() => handleDelete(event)}>Delete series</button>
                          </>
                        ) : (
                          <>
                            <button type="button" className="action-link" onClick={() => handleEdit(event)}>Edit</button>
                            <button type="button" className="action-link danger" onClick={() => handleDelete(event)}>Delete</button>
                          </>
                        )}
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </section>
        </div>
      </div>
    </section>
  );
}
